import * as React from 'react';
import { observer } from 'mobx-react';
import AuthStore from '../../store/Auth';
import Main from './main';
import { Box, Paper, Typography, Button, Container } from '@mui/material';
import dataStore from '../../store';
import Admin from '../admin';
import NotAccess from '../../components/NotAccess';
import CompanyLogo from '../../components/CompanyLogo';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { appSubtitle } from '../../constants/base';
import { Routes, Route, Navigate } from 'react-router-dom';
import UserProfile from './main/parts/UserProfile';

const AppSwitchPage = observer(() => {
  const [groups, setGroups] = React.useState<any[]>([]);
  const userGroups = AuthStore.user?.user?.profile?.['cognito:groups'] || [];

  const getConfigurationByGroup = async (group: string) => {
    await dataStore.getConfigurationByGroup(group);
  };

  React.useEffect(() => {
    if (AuthStore?.user?.isLoading) {
      return;
    }

    AuthStore.setAllGroups(userGroups);
    setGroups(userGroups);
    let localGroup = localStorage.getItem('selectedGroup');

    if (localGroup && !userGroups.includes(localGroup)) {
      localGroup = null;
    } else {
      if (userGroups.length === 1) {
        localGroup = userGroups[0] as string;
        localStorage.setItem('selectedGroup', localGroup);
      }

      if (localGroup) {
        AuthStore.setSelectGroup(localGroup);
        getConfigurationByGroup(localGroup);
      }
    }
  }, [userGroups.length]);

  if (AuthStore?.user?.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Container
          maxWidth='lg'
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CompanyLogo />
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Container
              maxWidth='xs'
              sx={{
                boxShadow: 0,
                backgroundColor: 'white',
                borderRadius: 6,
                p: 3,
                border: '2px solid #ff5f1f',
                fontFamily: "'Source Sans 3', sans-serif",
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <Typography variant='body1'>Loading...</Typography>
              </Box>
            </Container>
          </Box>
        </Container>
      </Box>
    );
  }

  if (userGroups.length === 0) {
    return <NotAccess />;
  }

  const onSelect = (group: string) => {
    AuthStore.setSelectGroup(group);
    localStorage.setItem('selectedGroup', group);
    getConfigurationByGroup(group);
  };

  const paperStyle = {
    width: 300,
    p: 4,
    textAlign: 'center',
    backgroundColor: '#ffffff',
    boxShadow: 0,
    border: '1px solid #ff5f1f',
  } as const;

  const selectionUI = (
    <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', p: 4 }}>
      {groups.includes('central-lab') ? (
        <Paper sx={paperStyle}>
          <Typography variant='h5'>Central Lab</Typography>
          <Button onClick={() => onSelect('central-lab')}>Enter</Button>
        </Paper>
      ) : null}

      {groups.includes('meta-map') ? (
        <Paper sx={paperStyle}>
          <Typography variant='h5'>MetaMAP</Typography>
          <Button onClick={() => onSelect('meta-map')}>Enter</Button>
        </Paper>
      ) : null}

      {groups.includes('credit-admins') ? (
        <Paper sx={paperStyle}>
          <Typography variant='h5'>Credit admin</Typography>
          <Button onClick={() => onSelect('credit-admins')}>Enter</Button>
        </Paper>
      ) : null}

      {groups.includes('sample-manager') ? (
        <Paper sx={paperStyle}>
          <Typography variant='h5'>Sample manager</Typography>
          <Button onClick={() => onSelect('sample-manager')}>Enter</Button>
        </Paper>
      ) : null}
    </Box>
  );

  const content = () => {
    if (AuthStore.selectedGroup && !dataStore.config && dataStore.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography variant='body1'>Loading...</Typography>
        </Box>
      );
    }

    const selected = AuthStore.selectedGroup ?? '';
    const isClientGroup = [
      'central-lab',
      'meta-map',
      'sample-manager',
    ].includes(selected);
    const isAdminGroup = selected === 'credit-admins';
    const ready = !!selected && !!dataStore.config;

    return (
      <Routes>
        <Route path='/profile' element={<UserProfile />} />

        {ready && isClientGroup && <Route path='/*' element={<Main />} />}

        {ready && isAdminGroup && (
          <>
            <Route path='/admin/*' element={<Admin />} />
            <Route path='/*' element={<Navigate to='/admin' replace />} />
          </>
        )}

        <Route
          path='*'
          element={
            ready ? (
              isClientGroup ? (
                <Navigate to='/search' replace />
              ) : (
                <Navigate to='/admin' replace />
              )
            ) : (
              selectionUI
            )
          }
        />
      </Routes>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <CompanyLogo />
        <Container
          sx={{
            boxShadow: 0,
            backgroundColor: 'white',
            borderRadius: 6,
            p: 3,
            marginTop: '35px',
            border: '2px solid #ff5f1f',
          }}
          maxWidth='md'
        >
          <Header description={appSubtitle} />
          {content()}
        </Container>
      </Container>
      <Footer auth={true} />
    </Box>
  );
});

export default AppSwitchPage;
